import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_SETTINGS } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  AntimalwareSettingsDeleteFilesOlderThanType,
  AntimlawareSettingsSubmitQuarantinedType,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled, toggleVisibility } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  AntimalwareSettingsFormValue,
  AntimalwareSettingsSearchableKeywords,
} from '../../../../utils/uiSettings/antimalware/antimalware.types';
import {
  DELETE_FILES_OLDER_THAN_DAYS_DEFAULT_VALUE,
  getAntimalwareSettingsDefaultValue,
  getAntimalwareSettingsDeleteFilesOlderThanTypeFormOptions,
  getAntimalwareSettingsSearchableKeywords,
  getAntimlawareSettingsSubmitQuarantinedTypeFormOptions,
  MAX_ANTIMALWARE_SETTINGS_CENTRALIZED_LOCATION_PASSWORD,
  MAX_ANTIMALWARE_SETTINGS_CENTRALIZED_LOCATION_USERNAME,
} from '../../../../utils/uiSettings/antimalware/antimalware.values';
import { FontSizes, FontWeights } from '../../../../../../../types/common/types';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { classMap } from 'lit/directives/class-map.js';
import { GzDropdownWebComponent } from '../../../../../../../ui/gzDropdown';
import { InputWidthValues } from '../../../../../../../ui/gzInput';
import { GzInputWebComponent } from '../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../ui/gzForm/validators/required.validator';
import { CustomizableValidator } from '../../../../../../../ui/gzForm/validators/customizable.validator';
import { pathNetwork } from '../../../../../../common/policyPanel/uiSettings/utils/validators/path.validators';
import { MaxLengthValidator } from '../../../../../../../ui/gzForm/validators/max-length.validator';
import { InfoBoxSeverity } from 'apps/webcomponents/src/ui/gzInfoBox/types';
import { Feature } from '@nirvana/shared/constants';
import {
  validateCredentialsUsername,
  validatePasswordComplexity,
} from '../../../../../../common/policyPanel/uiSettings/utils/validators/credentials.validators';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_SETTINGS)
export class GzAntimalwareSettingsWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  AntimalwareSettingsFormValue,
  AntimalwareSettingsSearchableKeywords
> {
  protected path = {
    moduleId: ModuleId.ANTIMALWARE,
    sectionId: SectionId.SETTINGS,
  };
  protected searchableKeywords = getAntimalwareSettingsSearchableKeywords();

  @query('#antimalware-settings-delete-field')
  private settingsDeleteField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#antimalware-settings-submit-enable-field')
  private settingsSubmitEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-settings-submit-hours-field')
  private settingsSubmitHoursField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#antimalware-settings-rescan-field')
  private settingsRescanField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-settings-copy-field')
  private settingsCopyField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-settings-allow-field')
  private settingsAllowField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-settings-centralized-enable-field')
  private centralizedEnableField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-settings-centralized-content')
  private centralizedContent: HTMLElement;
  @query('#antimalware-settings-centralized-archive-password-field')
  private centralizedArchivePasswordField: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#antimalware-settings-centralized-archive-password-confirmation-field')
  private centralizedArchivePasswordConfirmationField: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#antimalware-settings-centralized-location-path-field')
  private centralizedLocationPathField: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#antimalware-settings-centralized-location-username-field')
  private centralizedLocationUsernameField: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#antimalware-settings-centralized-location-password-field')
  private centralizedLocationPasswordField: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#antimalware-settings-centralized-submit-to-sandbox-field')
  private centralizedSubmitToSandboxField: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#antimalware-settings-centralized-submit-to-sandbox-info-box')
  private centralizedSubmitToSandboxInfoBox: HTMLElement;

  private isSandboxAnalyzerVisible = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_ANALYZER_VISIBLE]);
  private isSandboxLocalVisible = Application.Main.hasFeatures([Feature.PRODUCT_FEATURE_SANDBOX_LOCAL_VISIBLE]);
  private isCloud = Application.Main.product.isCloud();

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-title
      class="content"
      searchable-label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.Settings].label}"
      ><span>${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.Settings].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="${FontSizes.PARAGRAPH_12}" fontWeight="${FontWeights.NORMAL}" class="infoText"
      >${translateFn('Configure additional options for this policy.')}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="antimalware-settings-content">
        <gz-title
          fontWeight="${FontWeights.SEMIBOLD}"
          fontSize="${FontSizes.PARAGRAPH_12}"
          class="mt-4 subtitle"
          searchable-label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.Quarantine].label}"
          >${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.Quarantine].translatedLabel}</gz-title
        >

        <gz-form-field
          id="antimalware-settings-delete-field"
          class="mt-2"
          searchable-label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.DeleteFilesOlderThan]
            .label}"
          label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.DeleteFilesOlderThan].translatedLabel}"
        >
          <gz-dropdown
            gz-form-input
            .options="${getAntimalwareSettingsDeleteFilesOlderThanTypeFormOptions()}"
            .value="${AntimalwareSettingsDeleteFilesOlderThanType.SIXTY_DAYS}"
            labelWidth=${InputWidthValues.SMALL}
          ></gz-dropdown>
        </gz-form-field>

        <div class="checkbox-with-input">
          <gz-form-field class="checkbox-width" id="antimalware-settings-submit-enable-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.SubmitQuarantined]
                .label}"
              label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.SubmitQuarantined]
                .translatedLabel}"
              gz-form-input
              @change="${e => {
                toggleDisabled(this.settingsSubmitHoursField, !e.detail.checked);
              }}"
            ></gz-checkbox>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="antimalware-settings-submit-hours-field" class="ml-2">
            <gz-dropdown
              gz-form-input
              .options="${getAntimlawareSettingsSubmitQuarantinedTypeFormOptions()}"
              .value="${AntimlawareSettingsSubmitQuarantinedType.ONE_HOUR}"
              labelWidth=${InputWidthValues.SMALL}
            ></gz-dropdown>
          </gz-form-field>
        </div>

        <gz-form-field id="antimalware-settings-rescan-field"
          ><gz-checkbox
            searchable-label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.RescanQuarantine].label}"
            label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.RescanQuarantine].translatedLabel}"
            gz-form-input
          >
          </gz-checkbox>
        </gz-form-field>

        <gz-form-field id="antimalware-settings-copy-field" class="mt-2"
          ><gz-checkbox
            searchable-label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.CopyFilesToQuarantine]
              .label}"
            label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.CopyFilesToQuarantine]
              .translatedLabel}"
            gz-form-input
          >
          </gz-checkbox>
        </gz-form-field>

        <gz-form-field id="antimalware-settings-allow-field" class="mt-2"
          ><gz-checkbox
            searchable-label="${this.searchableKeywords[
              AntimalwareSettingsSearchableKeywords.AllowUserQuarantineActions
            ].label}"
            label="${this.searchableKeywords[AntimalwareSettingsSearchableKeywords.AllowUserQuarantineActions]
              .translatedLabel}"
            gz-form-input
          >
          </gz-checkbox>
        </gz-form-field>
      </div>

      <div class="mt-4 ${classMap({ hidden: this.isCloud })}">
        <gz-form-field id="antimalware-settings-centralized-enable-field">
          <gz-checkbox
            gz-form-input
            label="${translateFn('Centralized quarantine')}"
            class="checkbox-subtitle"
            @change="${e => this.toggleCentralizedContent(!e.detail.checked)}"
          ></gz-checkbox>
        </gz-form-field>

        <div id="antimalware-settings-centralized-content">
          <gz-form-field
            id="antimalware-settings-centralized-archive-password-field"
            class="label-mw-250 mt-2"
            label="${translateFn('Archive password')}"
            required
          >
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the archive password')}"
              type="password"
              @change="${e => this.centralizedArchivePasswordConfirmationField.validate(true)}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="antimalware-settings-centralized-archive-password-confirmation-field"
            class="label-mw-250 mt-2"
            label="${translateFn('Confirm password')}"
            required
          >
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Re-enter the archive password')}"
              type="password"
              @change="${e => this.centralizedArchivePasswordField.validate(true)}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="antimalware-settings-centralized-location-path-field"
            class="label-mw-250 mt-2"
            label="${translateFn('Network share path')}"
            required
          >
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the network share path')}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="antimalware-settings-centralized-location-username-field"
            class="label-mw-250 mt-2"
            label="${translateFn('Network share username')}"
            required
          >
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the network share username')}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="antimalware-settings-centralized-location-password-field"
            class="label-mw-250 mt-2"
            label="${translateFn('Network share password')}"
            required
          >
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the network share password')}"
              type="password"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field
            id="antimalware-settings-centralized-submit-to-sandbox-field"
            class="mt-2 ${classMap({ hidden: !this.isSandboxLocalVisible || !this.isSandboxAnalyzerVisible })}"
            ><gz-checkbox
              label="${translateFn('Automatically submit items from quarantine to a Sandbox Analyzer')}"
              gz-form-input
              @change="${e =>
                toggleVisibility(
                  this.centralizedSubmitToSandboxInfoBox,
                  this.isSandboxLocalVisible && this.isSandboxAnalyzerVisible && e.detail.checked
                )}"
            >
            </gz-checkbox>
          </gz-form-field>

          <gz-info-box
            id="antimalware-settings-centralized-submit-to-sandbox-info-box"
            severity="${InfoBoxSeverity.INFORMATIVE}"
            class="hidden mt-1"
            .innerHTML=${translateFn(
              'Before enabling this option, make sure that you have selected a local instance of Sandbox Analyzer in the {bold}Sandbox Analyzer > Endpoint Sensor{/bold} section of the policy settings.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-info-box>
        </div>
      </div>
    `;
  }

  private async toggleCentralizedContent(disabled: boolean) {
    await this.updateComplete;
    toggleVisibility(this.centralizedContent, !disabled);

    const commonArchivePasswordValidators = [
      CustomizableValidator(() => {
        const password = this.centralizedArchivePasswordField.value;
        const confirmation = this.centralizedArchivePasswordConfirmationField.value;

        return password === confirmation
          ? { valid: true }
          : { valid: false, error: translateFn('Passwords do not match.') };
      }),
    ];

    // reset validators for fields
    this.centralizedArchivePasswordField.Validators = disabled
      ? []
      : [
          RequiredValidator(translateFn('Required field')),
          CustomizableValidator(validatePasswordComplexity),
          ...commonArchivePasswordValidators,
        ];
    this.centralizedArchivePasswordField.validate(true);
    this.centralizedArchivePasswordConfirmationField.Validators = disabled
      ? []
      : [RequiredValidator(translateFn('Required field')), ...commonArchivePasswordValidators];
    this.centralizedArchivePasswordConfirmationField.validate(true);

    this.centralizedLocationPathField.Validators = disabled
      ? []
      : [
          RequiredValidator(translateFn('Required field')),
          CustomizableValidator(async (value: string) => {
            const isValidate = await pathNetwork(value);

            return isValidate ? { valid: true } : { valid: false, error: translateFn('Invalid path') };
          }),
        ];
    this.centralizedLocationPathField.validate(true);
    this.centralizedLocationUsernameField.Validators = disabled
      ? []
      : [
          RequiredValidator(translateFn('Required field')),
          CustomizableValidator(validateCredentialsUsername),
          MaxLengthValidator(
            MAX_ANTIMALWARE_SETTINGS_CENTRALIZED_LOCATION_USERNAME,
            translateFn('The username must not exceed {maxLength} characters.', {
              maxLength: MAX_ANTIMALWARE_SETTINGS_CENTRALIZED_LOCATION_USERNAME,
            })
          ),
        ];
    this.centralizedLocationUsernameField.validate(true);

    this.centralizedLocationPasswordField.Validators = disabled
      ? []
      : [
          RequiredValidator(translateFn('Required field')),
          MaxLengthValidator(
            MAX_ANTIMALWARE_SETTINGS_CENTRALIZED_LOCATION_PASSWORD,
            translateFn('The password must not exceed {maxLength} characters.', {
              maxLength: MAX_ANTIMALWARE_SETTINGS_CENTRALIZED_LOCATION_PASSWORD,
            })
          ),
        ];
    this.centralizedLocationPasswordField.validate(true);
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.antimalware.securityServers
   */
  protected getValueFromManager(): AntimalwareSettingsFormValue {
    // get default value
    const defaultValue = getAntimalwareSettingsDefaultValue();

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ANTIMALWARE]: {
          settings: {
            deleteFilesOlderThan: PolicyPanelManager.getInstance().getPolicy(
              'uiSettings.antimalware.settings.deleteFilesOlderThan',
              DELETE_FILES_OLDER_THAN_DAYS_DEFAULT_VALUE
            ),
            submitQuarantined: PolicyPanelManager.getInstance().getPolicy(
              'uiSettings.antimalware.settings.submitQuarantined',
              {}
            ),
            rescanQuarantine: PolicyPanelManager.getInstance().getPolicy(
              'uiSettings.antimalware.settings.rescanQuarantine',
              true
            ),
            copyFilesToQuarantineBeforeDisinfect: PolicyPanelManager.getInstance().getPolicy(
              'uiSettings.antimalware.settings.copyFilesToQuarantineBeforeDisinfect',
              true
            ),
            allowUserQuarantineActions: PolicyPanelManager.getInstance().getPolicy(
              'uiSettings.antimalware.settings.allowUserQuarantineActions',
              true
            ),
            centralizedQuarantine: PolicyPanelManager.getInstance().getPolicy(
              'uiSettings.antimalware.settings.centralizedQuarantine',
              {}
            ),
          },
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        antimalware: {
          settings: {
            deleteFilesOlderThan: this.settingsDeleteField,
            submitQuarantined: {
              enable: this.settingsSubmitEnableField,
              hours: this.settingsSubmitHoursField,
            },
            rescanQuarantine: this.settingsRescanField,
            copyFilesToQuarantineBeforeDisinfect: this.settingsCopyField,
            allowUserQuarantineActions: this.settingsAllowField,
            centralizedQuarantine: {
              enable: this.centralizedEnableField,
              archivePassword: this.centralizedArchivePasswordField,
              location: {
                path: this.centralizedLocationPathField,
                username: this.centralizedLocationUsernameField,
                password: this.centralizedLocationPasswordField,
              },
              autoSubmitToSandboxAnalyzer: this.centralizedSubmitToSandboxField,
            },
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        antimalware: {
          settings: this.valueT.uiSettings.antimalware.settings,
        },
      },
    };

    this.centralizedArchivePasswordConfirmationField.value =
      this.valueT.uiSettings.antimalware.settings.centralizedQuarantine?.archivePassword;
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    // unset centralizedQuarantine field if cloud
    if (this.isCloud) {
      _.unset(value, ['uiSettings', 'antimalware', 'settings', 'centralizedQuarantine']);
    }

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'antimalware', 'settings'], {});
    }

    return value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_SETTINGS: GzAntimalwareSettingsWebComponent;
  }
}
